import React, { useState, useEffect, useRef } from 'react';
import { Send, Paperclip, X, Image, File, ChevronLeft } from 'lucide-react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { Message, Conversation, Participant } from '../../hooks/useChat';

// Function to upload chat attachment to Supabase Storage
const uploadChatAttachment = async (file: File, conversationId: string): Promise<{ url: string; type: string } | null> => {
  try {
    // Create a unique filename to prevent conflicts
    const fileExt = file.name.split('.').pop();
    const fileName = `${conversationId}/${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;
    
    // Upload the file to Supabase Storage
    const { data, error } = await supabase.storage
      .from('chat-attachments')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });
    
    if (error) throw error;
    
    // Get the public URL for the uploaded file
    const { data: { publicUrl } } = supabase.storage
      .from('chat-attachments')
      .getPublicUrl(data.path);
    
    // Determine attachment type
    let attachmentType = 'file';
    if (file.type.startsWith('image/')) {
      attachmentType = 'image';
    }
    
    return { url: publicUrl, type: attachmentType };
  } catch (error) {
    console.error('Error uploading attachment:', error);
    return null;
  }
};
interface ChatWindowProps {
  conversation: Conversation | null;
  messages: Message[];
  onSendMessage: (content: string, attachmentUrl?: string, attachmentType?: string) => Promise<void>;
  onBack?: () => void;
  onClose?: () => void;
  loading?: boolean;
}

const ChatWindow: React.FC<ChatWindowProps> = ({ 
  conversation, 
  messages, 
  onSendMessage, 
  onBack,
  onClose,
  loading = false
}) => {
  const { user } = useSupabase();
  const [messageInput, setMessageInput] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!messageInput.trim() && !attachment) return;
    
    try {
      let attachmentUrl = null;
      let attachmentType = null;
      
      if (attachment) {
        // Show loading state
        setMessageInput('Uploading attachment...');
        
        // Upload the attachment to Supabase Storage
        const uploadResult = await uploadChatAttachment(attachment, conversation.id);
        
        if (uploadResult) {
          attachmentUrl = uploadResult.url;
          attachmentType = uploadResult.type;
        } else {
          // If upload failed, show error message
          setMessageInput('Failed to upload attachment. Please try again.');
          return;
        }
      }
      
      // Reset message input if it was changed during upload
      if (messageInput === 'Uploading attachment...' || messageInput === 'Failed to upload attachment. Please try again.') {
        setMessageInput('');
      }
      
      await onSendMessage(messageInput, attachmentUrl, attachmentType);
      setMessageInput('');
      setAttachment(null);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleAttachmentClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAttachment(file);
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getParticipantName = (participantId: string): string => {
    if (!conversation) return 'Unknown';
    
    const participant = conversation.participants.find(p => p.user_id === participantId);
    if (!participant || !participant.user) return 'Unknown';
    
    return `${participant.user.first_name} ${participant.user.last_name}`;
  };

  const getParticipantRole = (participantId: string): string => {
    if (!conversation) return '';
    
    const participant = conversation.participants.find(p => p.user_id === participantId);
    return participant?.role || '';
  };

  const getOtherParticipants = (): Participant[] => {
    if (!conversation || !user) return [];
    return conversation.participants.filter(p => p.user_id !== user.id);
  };

  if (!conversation) {
    return (
      <div className="flex flex-col h-full bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex-1 flex items-center justify-center p-6">
          <p className="text-gray-500 text-center">Select a conversation to start chatting</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-white border-b border-gray-200 flex items-center">
        {onBack && (
          <button 
            onClick={onBack}
            className="mr-2 p-1 rounded-full hover:bg-gray-100 md:hidden"
          >
            <ChevronLeft className="h-5 w-5 text-gray-500" />
          </button>
        )}
        <div className="flex-1">
          <div className="font-medium text-gray-900">
            {conversation.topic || getOtherParticipants().map(p => 
              p.user ? `${p.user.first_name} ${p.user.last_name}` : 'Unknown'
            ).join(', ')}
          </div>
          <div className="text-xs text-gray-500">
            {getOtherParticipants().map(p => p.role).join(', ')}
          </div>
        </div>
        {onClose && (
          <button 
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100"
            title="Close chat"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        )}
      </div>
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex justify-center items-center h-full">
            <p className="text-gray-500">No messages yet. Start the conversation!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => {
              const isOwnMessage = message.sender_id === user?.id;
              const senderName = isOwnMessage ? 'You' : getParticipantName(message.sender_id);
              const senderRole = getParticipantRole(message.sender_id);
              
              return (
                <div 
                  key={message.id} 
                  className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                >
                  <div 
                    className={`max-w-[75%] rounded-lg px-4 py-2 ${
                      isOwnMessage 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-white border border-gray-200 text-gray-800'
                    }`}
                  >
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-medium text-sm">
                        {senderName}
                      </span>
                      {senderRole && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          senderRole === 'buyer' 
                            ? 'bg-blue-100 text-blue-800' 
                            : senderRole === 'seller'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {senderRole}
                        </span>
                      )}
                    </div>
                    <p className="whitespace-pre-wrap break-words">{message.content}</p>
                    {message.attachment_url && (
                      <div className="mt-2">
                        {message.attachment_type === 'image' ? (
                          <img 
                            src={message.attachment_url} 
                            alt="Attachment" 
                            className="max-w-full rounded-md"
                          />
                        ) : (
                          <a 
                            href={message.attachment_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center space-x-2 text-sm underline"
                          >
                            <File className="h-4 w-4" />
                            <span>Attachment</span>
                          </a>
                        )}
                      </div>
                    )}
                    <div className="text-xs mt-1 opacity-70">
                      {new Date(message.created_at).toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>
      
      {/* Attachment Preview */}
      {attachment && (
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center space-x-2 bg-white p-2 rounded-md border border-gray-200">
            {attachment.type.startsWith('image/') ? (
              <Image className="h-5 w-5 text-blue-500" />
            ) : (
              <File className="h-5 w-5 text-orange-500" />
            )}
            <span className="text-sm truncate flex-1">{attachment.name}</span>
            <button 
              onClick={removeAttachment}
              className="p-1 hover:bg-gray-100 rounded-full"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>
        </div>
      )}
      
      {/* Message Input */}
      <div className="px-4 py-3 bg-white border-t border-gray-200">
        <div className="flex items-center space-x-2">
          <button 
            onClick={handleAttachmentClick}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full"
          >
            <Paperclip className="h-5 w-5" />
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
          />
          <div className="flex-1 relative">
            <textarea
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Type a message..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
              rows={1}
              style={{ minHeight: '40px', maxHeight: '120px' }}
            />
          </div>
          <button 
            onClick={handleSendMessage}
            disabled={!messageInput.trim() && !attachment}
            className={`p-2 rounded-full ${
              !messageInput.trim() && !attachment
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-orange-500 text-white hover:bg-orange-600'
            }`}
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;